import { FC, useCallback, useEffect, useState, useRef, useMemo } from "react";
import HeaderFilterSection from "components/HeaderFilterSection";
import ProductCard from "components/ProductCard";
import { CategoryData, Product } from "data/data";
import { useCartDataProvider } from "../../context/CartProductContext";
import myrealimg from "images/default-pasta.jpg";
import { useMenuSchedule } from "context/MenuScheduleContext";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { useOnlineOrderSettings } from "context/OnlineOrderSettingsContext";
import { useUI } from "context/UIContext";
import { ProductCardSkeleton } from "shared/Skeleton/Skeleton";

export interface PageHomeMenuProps {
  data?: Product[];
}

interface OnlineOrderSettings {
  logo?: string;
  orderLeadTime?: number;
  image?: string;
  titleText?: string;
  customTextOne?: string;
  pauseOnlineOrder?: boolean;
  pauseOnlineOrderLabel?: string;
  restaurantId?: string;
  isDelivery?: boolean;
}

const PageHomeMenu: FC<PageHomeMenuProps> = () => {
  const [categoryData, setCategoryData] = useState<CategoryData[]>([]);
  const { data: menuScheduleData } = useMenuSchedule();
  const [hasError, setHasError] = useState(false);
  const { searchProduct } = useCartDataProvider();
  const [loading, setLoading] = useState(true);
  const { settings, items } = useOnlineOrderSettings();
  const [onlineOrderSettings, setOnlineOrderSettings] =
    useState<OnlineOrderSettings | null>(null);
  const { openCustomization } = useUI();

  // Active category for tab highlight (null = "All")
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // Timestamp of last programmatic scroll -- scroll spy ignores events within this window
  const programmaticScrollUntil = useRef<number>(0);

  const imageUrl = myrealimg;

  const getAllCategoryName = async () => {
    try {
      const MERCHANT_STORAGE_KEY = "merchantname";
      const newmwechantname = Cookies.get(MERCHANT_STORAGE_KEY) ?? "";
      Cookies.set("merchantname", newmwechantname);
      setCategoryData(items as CategoryData[]);
    } catch (error) {
      setHasError(true);
    }
  };

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      if (settings && items) {
        const onlineData = settings as OnlineOrderSettings;
        setOnlineOrderSettings({
          ...onlineData,
          customTextOne: onlineData.customTextOne ?? "",
        });
        if (onlineData?.logo) {
          sessionStorage.setItem("onlineOrderSettingsLogo", onlineData.logo);
        }
        if (onlineData?.orderLeadTime) {
          Cookies.set("onlineOrderSettingsLeadTime", onlineData.orderLeadTime.toString());
        }
        if (onlineData?.titleText) {
          Cookies.set("onlineOrderSettingsTitleText", onlineData.titleText);
          Cookies.set("onlineOrderSettingstitleText", onlineData.titleText);
        }
        if (onlineData?.pauseOnlineOrder) {
          Cookies.set("onlineOrderSettingsPause", onlineData.pauseOnlineOrder.toString());
        }
        if (onlineData?.pauseOnlineOrderLabel) {
          Cookies.set("onlineOrderSettingsPauseLabel", onlineData.pauseOnlineOrderLabel.toString());
        }
        if (onlineData?.restaurantId) {
          Cookies.set("restaurantId", onlineData.restaurantId.toString());
        }
        if (onlineData?.isDelivery) {
          Cookies.set("isDelivery", onlineData.isDelivery.toString() ?? "false");
        }
      }
    } catch (error) {
      setHasError(true);
    } finally {
      setLoading(false);
    }
  }, [items, settings]);

  useEffect(() => {
    const fetchData = async () => {
      await getAllCategoryName();
      await fetchSettings();
    };
    fetchData();
  }, [items, settings]);

  const navigate = useNavigate();
  useEffect(() => {
    const merchantname = Cookies.get("merchantname");
    if (hasError) {
      navigate(`/${merchantname}/Page404`);
    }
  }, [hasError]);

  // Filter categories: only those with items, sorted items A-Z
  const filteredCategories = useMemo(() => {
    if (!categoryData || categoryData.length === 0) return [];
    return categoryData
      .filter((cat) => cat.items?.length > 0)
      .map((cat) => ({
        ...cat,
        items: [...cat.items]
          .filter((item: Product) => item.isAvailable === 2 || item.isAvailable === 3)
          .sort((a: Product, b: Product) => a.name.localeCompare(b.name)),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [categoryData]);

  // Apply search filter per category
  const searchFilteredCategories = useMemo(() => {
    if (!searchProduct) return filteredCategories;
    return filteredCategories
      .map((cat) => ({
        ...cat,
        items: cat.items.filter((item: Product) =>
          item.name?.toLowerCase().includes(searchProduct.toLowerCase())
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [filteredCategories, searchProduct]);

  // Handle category tab click: scroll to section
  const handleCategoryClick = useCallback((categoryId: number | null) => {
    // Block scroll spy for 1.2 seconds
    programmaticScrollUntil.current = Date.now() + 1200;
    setActiveCategoryId(categoryId);

    const mainEl = document.querySelector("main");
    if (!mainEl) return;

    if (categoryId === null) {
      mainEl.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Use requestAnimationFrame to ensure refs are populated after render
    requestAnimationFrame(() => {
      const sectionEl = sectionRefs.current[categoryId];
      if (!sectionEl) return;

      const stickyTabsHeight = 52;
      const sectionRect = sectionEl.getBoundingClientRect();
      const mainRect = mainEl.getBoundingClientRect();
      const scrollTarget =
        mainEl.scrollTop + (sectionRect.top - mainRect.top) - stickyTabsHeight;
      mainEl.scrollTo({ top: scrollTarget, behavior: "smooth" });
    });
  }, []);

  // Scroll spy: update active tab as user scrolls manually
  useEffect(() => {
    const mainEl = document.querySelector("main");
    if (!mainEl || filteredCategories.length === 0) return;

    let rafId = 0;
    const handleScroll = () => {
      // Skip if we're inside a programmatic scroll window
      if (Date.now() < programmaticScrollUntil.current) return;

      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        // Double-check after RAF in case a click happened between schedule and execution
        if (Date.now() < programmaticScrollUntil.current) return;

        const offset = 80;
        let currentId: number | null = null;

        for (const cat of filteredCategories) {
          const el = sectionRefs.current[cat.id];
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top <= offset) {
              currentId = cat.id;
            }
          }
        }

        setActiveCategoryId(currentId);
      });
    };

    mainEl.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      mainEl.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [filteredCategories]);

  const handleCustomize = (product: Product) => {
    openCustomization(product);
  };

  return (
    <div className="nc-PageHomeMenu relative bg-slate-50 dark:bg-slate-950 min-h-screen">
      <HeaderFilterSection
        menuScheduleData={menuScheduleData ?? []}
        categoryData={categoryData}
        imageUrl={imageUrl}
        onlineOrderSettings={onlineOrderSettings}
        loading={loading}
        activeCategoryId={activeCategoryId}
        onCategoryClick={handleCategoryClick}
      />

      {/* All categories with section headers */}
      <div className="px-4 pb-4">
        {loading ? (
          <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        ) : searchFilteredCategories.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              No items found
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Try a different search or category
            </p>
          </div>
        ) : (
          searchFilteredCategories.map((category) => (
            <div
              key={category.id}
              ref={(el) => { sectionRefs.current[category.id] = el; }}
              className="mb-6"
            >
              {/* Category section header */}
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 pt-4 pb-3 sticky top-[52px] z-20 bg-slate-50 dark:bg-slate-950">
                {category.name}
              </h2>

              {/* Items grid */}
              <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                {category.items.map((item: Product, index: number) => (
                  <ProductCard
                    categoryData={categoryData}
                    data={item}
                    key={item.id || index}
                    onCustomize={handleCustomize}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PageHomeMenu;
